import React, { useEffect, useState } from "react";
import { Shield, Check, X, RefreshCw, AlertTriangle } from "lucide-react";
import { aiApi, formatApiError } from "../../lib/ledgerApi";
import { toast } from "sonner";

const SEVERITY = {
  block: "border-status-tampered/40 bg-status-tampered/10 text-status-tampered",
  hold: "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400",
  allow_medium: "border-status-info/40 bg-status-info/10 text-status-info",
  allow_low: "border-status-ok/40 bg-status-ok/10 text-status-ok",
};

const fmt = (s) => {
  try {
    const n = Number(s);
    return Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: 6 }) : s;
  } catch {
    return s;
  }
};

export default function Holds() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setItems(await aiApi.myHolds());
    } catch (e) {
      toast.error("Failed to load holds", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const i = setInterval(load, 5000);
    return () => clearInterval(i);
  }, []);

  const onApprove = async (id) => {
    setBusy(id);
    try {
      const r = await aiApi.approveHold(id);
      if (r.executed) toast.success("Approved & executed", { description: "Tx hash: " + r.tx_id?.slice(0, 16) + "…" });
      else toast.success("Approved", { description: r.note || "" });
      await load();
    } catch (e) {
      toast.error("Approve failed", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setBusy(null);
    }
  };

  const onReject = async (id) => {
    setBusy(id);
    try {
      await aiApi.rejectHold(id);
      toast.success("Rejected");
      await load();
    } catch (e) {
      toast.error("Reject failed", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="max-w-6xl px-6 md:px-10 py-10 md:py-14" data-testid="holds-page">
      <div className="mb-8 flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">/ AI · Review queue</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-medium flex items-center gap-3">
            <Shield className="w-6 h-6 text-muted-foreground" strokeWidth={1.5} /> Holds
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">
            Transfers flagged for human review by the fraud + compliance pipeline. The ledger is unaffected until you decide.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="holds-refresh"
          className="inline-flex items-center gap-1.5 px-3 py-2 text-xs border border-border rounded-sm hover:border-foreground"
        >
          <RefreshCw className="w-3 h-3" strokeWidth={1.5} /> Refresh
        </button>
      </div>

      {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
      {!loading && items.length === 0 && (
        <div className="border border-border rounded-sm bg-card p-8 text-center text-muted-foreground text-sm">
          No transfers held. Your activity looks clean.
        </div>
      )}

      <div className="space-y-3">
        {items.map((h) => {
          const v = h.verdict || {};
          const pending = h.status === "pending";
          return (
            <div
              key={h.id}
              data-testid={`hold-${h.id}`}
              className="border border-border rounded-sm bg-card p-5 space-y-4"
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">{h.kind?.replace("_", " ")}</div>
                  <div className="text-lg">
                    <span className="font-mono">{fmt(h.amount)} {h.currency}</span>
                    <span className="text-muted-foreground"> → </span>
                    <span>{h.to_legal_name}</span>
                    <span className="text-muted-foreground font-mono text-xs"> ({h.to_vpa})</span>
                  </div>
                  {h.memo && <div className="text-[12px] text-muted-foreground mt-1">“{h.memo}”</div>}
                  <div className="text-[11px] font-mono text-muted-foreground mt-1">{new Date(h.created_at).toLocaleString()}</div>
                </div>
                <div className="text-right">
                  <div className={`inline-flex items-center gap-2 px-2 py-1 rounded-sm border text-[11px] font-mono ${SEVERITY[v.decision] || ""}`}>
                    risk {v.final_score ?? "?"}/100 · {v.decision || "?"}
                  </div>
                  <div className={`mt-1 text-[10px] uppercase tracking-[0.2em] ${pending ? "text-amber-600 dark:text-amber-400" : "text-muted-foreground"}`}>
                    {h.status}{h.decided_by ? ` · by ${h.decided_by}` : ""}
                  </div>
                </div>
              </div>

              <div className="text-[12px] text-muted-foreground border-t border-border pt-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-3 h-3 mt-0.5 text-amber-500" strokeWidth={1.5} />
                  <span>{v.rationale || "No rationale."}</span>
                </div>
                {v.signals?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {v.signals.map((s, i) => (
                      <span key={i} className="px-1.5 py-0.5 border border-border bg-muted rounded-sm font-mono text-[10px]">
                        {s.name}{s.value !== undefined && s.value !== true ? `=${typeof s.value === "object" ? "…" : s.value}` : ""}
                      </span>
                    ))}
                  </div>
                )}
                {v.compliance_recipient?.matches?.length > 0 && (
                  <div className="mt-2 text-[11px]">
                    <span className="text-status-tampered">Compliance recipient match:</span>{" "}
                    {v.compliance_recipient.matches.slice(0, 2).map((m) => m.name).join(", ")}
                  </div>
                )}
              </div>

              {pending && (
                <div className="flex gap-2 justify-end pt-2 border-t border-border">
                  <button
                    onClick={() => onReject(h.id)}
                    disabled={busy === h.id}
                    data-testid={`hold-reject-${h.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-xs border border-border rounded-sm hover:border-status-tampered hover:text-status-tampered transition-colors disabled:opacity-50"
                  >
                    <X className="w-3 h-3" strokeWidth={1.5} /> Reject
                  </button>
                  <button
                    onClick={() => onApprove(h.id)}
                    disabled={busy === h.id}
                    data-testid={`hold-approve-${h.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-xs border border-foreground bg-foreground text-background rounded-sm hover:opacity-90 disabled:opacity-50"
                  >
                    <Check className="w-3 h-3" strokeWidth={1.5} /> Approve & execute
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
